import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Form, Row, Col, Badge, Modal, Spinner, InputGroup, ProgressBar, Nav, Alert } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

// ── Komponen PaginationBar Modern & Compact ──
const PaginationBar = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemLabel = 'data',
  itemsPerPageOptions = [10, 25, 50, 100],
  activeColor = '#0284c7'
}) => {
  const [jumpVal, setJumpVal] = useState('');

  const startIdx = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIdx = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const pages = getPageNumbers();

  const handleJump = (e) => {
    e?.preventDefault?.();
    const p = parseInt(jumpVal, 10);
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      onPageChange(p);
      setJumpVal('');
    }
  };

  if (totalItems === 0) return null;

  return (
    <div
      style={{
        padding: '6px 14px',
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        fontSize: '0.72rem'
      }}
    >
      {/* Sisi Kiri: Info Jumlah Baris & Dropdown Page Size */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: '#64748b' }}>
          Menampilkan <strong style={{ color: '#0f172a' }}>{startIdx.toLocaleString('id-ID')}</strong> - <strong style={{ color: '#0f172a' }}>{endIdx.toLocaleString('id-ID')}</strong> dari <strong style={{ color: '#0f172a' }}>{totalItems.toLocaleString('id-ID')}</strong> {itemLabel}
        </span>

        {onItemsPerPageChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b' }}>
            <span>Tampilkan</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '2px 6px',
                borderRadius: 4,
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#0f172a',
                fontSize: '0.7rem',
                fontWeight: 600,
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {itemsPerPageOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span>baris</span>
          </div>
        )}
      </div>

      {/* Sisi Kanan: Navigasi Tombol Page & Jump */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {totalPages > 3 && (
          <form onSubmit={handleJump} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Hal:</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpVal}
              onChange={(e) => setJumpVal(e.target.value)}
              placeholder={String(currentPage)}
              style={{
                width: 42,
                padding: '2px 4px',
                borderRadius: 4,
                border: '1px solid #cbd5e1',
                textAlign: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
                outline: 'none'
              }}
            />
            <button
              type="submit"
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                padding: '2px 6px',
                fontSize: '0.68rem',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer'
              }}
            >
              Go
            </button>
          </form>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* First Page */}
          <button
            type="button"
            title="Halaman Pertama"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            style={{
              width: 26,
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#ffffff',
              color: currentPage === 1 ? '#cbd5e1' : '#475569',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.7rem'
            }}
          >
            <i className="bi bi-chevron-double-left" />
          </button>

          {/* Prev Page */}
          <button
            type="button"
            title="Halaman Sebelumnya"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            style={{
              width: 26,
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#ffffff',
              color: currentPage === 1 ? '#cbd5e1' : '#475569',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.7rem'
            }}
          >
            <i className="bi bi-chevron-left" />
          </button>

          {/* Page Number Buttons */}
          {pages.map((p, i) => {
            if (p === '...') {
              return (
                <span key={`dots-${i}`} style={{ padding: '0 3px', color: '#94a3b8', fontSize: '0.72rem' }}>
                  &hellip;
                </span>
              );
            }
            const isActive = p === currentPage;
            return (
              <button
                type="button"
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  minWidth: 26,
                  height: 26,
                  padding: '0 5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${isActive ? activeColor : '#cbd5e1'}`,
                  borderRadius: 4,
                  background: isActive ? activeColor : '#ffffff',
                  color: isActive ? '#ffffff' : '#1e293b',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                }}
              >
                {p}
              </button>
            );
          })}

          {/* Next Page */}
          <button
            type="button"
            title="Halaman Selanjutnya"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              width: 26,
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#ffffff',
              color: currentPage === totalPages || totalPages === 0 ? '#cbd5e1' : '#475569',
              cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.7rem'
            }}
          >
            <i className="bi bi-chevron-right" />
          </button>

          {/* Last Page */}
          <button
            type="button"
            title="Halaman Terakhir"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              width: 26,
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#ffffff',
              color: currentPage === totalPages || totalPages === 0 ? '#cbd5e1' : '#475569',
              cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.7rem'
            }}
          >
            <i className="bi bi-chevron-double-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CekKelengkapanTagihan = () => {
  const navigate = useNavigate();
  const apiUrl = getApiUrl();

  // ── Header & Operator Info ──
  const [username, setUsername] = useState('Administrator');
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── Form Filter State ──
  // Default format DD/MM/YYYY
  const getTodayDmy = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [periode1, setPeriode1] = useState(getTodayDmy());
  const [periode2, setPeriode2] = useState(getTodayDmy());
  const [kdKlasifikasi, setKdKlasifikasi] = useState('');
  const [nmKlasifikasi, setNmKlasifikasi] = useState('');
  const lokasiLaporan = 'Preview Langsung di Halaman (PDF Viewer / Modal)';

  // ── Master Klasifikasi State ──
  const [klasifikasiList, setKlasifikasiList] = useState([]);
  const [showModalKlasifikasi, setShowModalKlasifikasi] = useState(false);
  const [searchKlasifikasi, setSearchKlasifikasi] = useState('');
  const [isLoadingKlasifikasi, setIsLoadingKlasifikasi] = useState(false);

  // ── Data Hasil Pengecekan ──
  const [dataResume, setDataResume] = useState([]);
  const [dataLab, setDataLab] = useState([]);
  const [dataRad, setDataRad] = useState([]);
  const [counts, setCounts] = useState({ resume: 0, lab: 0, rad: 0, total: 0 });

  // ── Status & Progress State ──
  const [isLoading, setIsLoading] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [progressText, setProgressText] = useState('0%');
  const [statusMsg, setStatusMsg] = useState({ show: false, variant: 'info', text: '' });
  const [hasSearched, setHasSearched] = useState(false);

  // ── Active Tab & Filter Pencarian Tabel ──
  const [activeTab, setActiveTab] = useState('resume'); // 'resume' | 'lab' | 'rad' | 'pdf'
  const [tableSearch, setTableSearch] = useState('');

  // ── Pagination State ──
  const [pageResume, setPageResume] = useState(1);
  const [pageLab, setPageLab] = useState(1);
  const [pageRad, setPageRad] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [pageKlasifikasi, setPageKlasifikasi] = useState(1);
  const itemsPerPageKlasifikasi = 8;

  // ── Inline PDF Viewer State (Menampilkan PDF tanpa membuka page baru) ──
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfProgressVal, setPdfProgressVal] = useState(0);
  const [pdfProgressText, setPdfProgressText] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Refs untuk keyboard navigation
  const inputPeriode1Ref = useRef(null);
  const inputPeriode2Ref = useRef(null);
  const inputKlasRef = useRef(null);

  // ── Clock effect & Initial Data ──
  const showToast = (text, variant = 'info', timeout = 4000) => {
    setStatusMsg({ show: true, variant, text });
    if (timeout) {
      setTimeout(() => setStatusMsg({ show: false, variant: 'info', text: '' }), timeout);
    }
  };

  const fetchKlasifikasi = useCallback(async () => {
    try {
      setIsLoadingKlasifikasi(true);
      const res = await fetch(`${apiUrl}/get_klasifikasi_tagihan`);
      const json = await res.json();
      if (json.status === 1 && Array.isArray(json.data)) {
        setKlasifikasiList(json.data);
      }
    } catch (err) {
      console.warn('Gagal memuat klasifikasi:', err);
    } finally {
      setIsLoadingKlasifikasi(false);
    }
  }, [apiUrl]);

  // ── Clock effect & Initial Data ──
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);

    const updateClock = () => {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: now.toLocaleDateString('id-ID', options),
        clock: now.toLocaleTimeString('id-ID') + ' WIB'
      });
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);

    // Ambil master klasifikasi
    fetchKlasifikasi();

    return () => clearInterval(interval);
  }, [fetchKlasifikasi]);

  // ── Handle Pilih Klasifikasi dari Modal ──
  const handleSelectKlasifikasi = (item) => {
    setKdKlasifikasi(item.kd_klas || '');
    setNmKlasifikasi(item.nm_klas || '');
    setShowModalKlasifikasi(false);
  };

  // ── Handle Input Klasifikasi Manual / Enter ──
  const handleKlasifikasiKeyDown = (e) => {
    if (e.key === 'Enter') {
      const val = kdKlasifikasi.trim();
      if (!val) {
        setShowModalKlasifikasi(true);
      } else {
        const found = klasifikasiList.find(k => k.kd_klas.toLowerCase() === val.toLowerCase());
        if (found) {
          setNmKlasifikasi(found.nm_klas);
        } else {
          setShowModalKlasifikasi(true);
        }
      }
    }
  };

  // ── Proses Pengecekan Kelengkapan Tagihan ──
  const handleProsesPengecekan = async () => {
    if (!periode1.trim()) {
      showToast('Tanggal awal periode validasi harus diisi!', 'danger');
      if (inputPeriode1Ref.current) inputPeriode1Ref.current.focus();
      return;
    }
    if (!periode2.trim()) {
      showToast('Tanggal akhir periode validasi harus diisi!', 'danger');
      if (inputPeriode2Ref.current) inputPeriode2Ref.current.focus();
      return;
    }

    setIsLoading(true);
    setProgressVal(15);
    setProgressText('15% - Menghubungkan database...');
    setDataResume([]);
    setDataLab([]);
    setDataRad([]);
    setHasSearched(false);

    try {
      setProgressVal(40);
      setProgressText('40% - Mengecek resume medis & verifikasi tindakan...');

      const params = new URLSearchParams({
        periode1: periode1.trim(),
        periode2: periode2.trim(),
        kd_klasifikasi: kdKlasifikasi.trim()
      });

      const res = await fetch(`${apiUrl}/periksa_kelengkapan_tagihan?${params.toString()}`);
      setProgressVal(75);
      setProgressText('75% - Mengompilasi data ketidaklengkapan tagihan...');

      const json = await res.json();

      if (json.status === 1) {
        setDataResume(json.data_resume || []);
        setDataLab(json.data_lab || []);
        setDataRad(json.data_rad || []);
        setCounts(json.counts || { resume: 0, lab: 0, rad: 0, total: 0 });
        if (json.nm_klasifikasi) setNmKlasifikasi(json.nm_klasifikasi);

        setProgressVal(100);
        setProgressText('100% Selesai');
        setHasSearched(true);

        const tot = json.counts ? json.counts.total : 0;
        if (tot > 0) {
          showToast(`Pengecekan selesai! Ditemukan ${tot} tagihan belum lengkap (Resume: ${json.counts.resume}, Lab: ${json.counts.lab}, Rad: ${json.counts.rad}).`, 'warning');
        } else {
          showToast('Pengecekan selesai! Semua tagihan pada periode ini LENGKAP (Nihil kekurangan).', 'success');
        }

        // Reset preview PDF agar siap dibuat ulang sesuai data pengecekan terbaru
        setPdfBlobUrl('');
      } else {
        setProgressVal(0);
        setProgressText('0%');
        showToast(json.msg || 'Gagal memproses pengecekan kelengkapan tagihan.', 'danger');
      }
    } catch (err) {
      setProgressVal(0);
      setProgressText('0%');
      showToast(`Kesalahan jaringan server: ${err.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Cetak / Lihat Dokumen PDF dengan Indikator Progress Bar Riil ──
  const handleGeneratePdf = async (targetView = 'modal', forceReload = false) => {
    if (!periode1.trim() || !periode2.trim()) {
      showToast('Periode tanggal validasi harus diisi terlebih dahulu.', 'warning');
      return;
    }

    if (targetView === 'modal') {
      setShowPdfModal(true);
    } else if (targetView === 'tab') {
      setActiveTab('pdf');
    }

    // Jika PDF sudah pernah digenerate dan bukan forceReload, tidak perlu fetch ulang
    if (pdfBlobUrl && !forceReload) {
      return;
    }

    setIsGeneratingPdf(true);
    setPdfProgressVal(15);
    setPdfProgressText('Menyiapkan parameter dokumen PDF...');
    setProgressVal(15);
    setProgressText('Menyiapkan PDF (15%)...');

    let currentProgress = 15;
    const progressTimer = setInterval(() => {
      currentProgress += Math.floor(Math.random() * 12) + 8;
      if (currentProgress > 88) currentProgress = 88;

      setPdfProgressVal(currentProgress);
      setProgressVal(currentProgress);

      if (currentProgress < 35) {
        setPdfProgressText('Menghubungkan ke server backend...');
        setProgressText(`Menghubungkan server (${currentProgress}%)...`);
      } else if (currentProgress < 65) {
        setPdfProgressText('Mengumpulkan rekap tagihan (Resume, Lab, Radiologi)...');
        setProgressText(`Rekap tagihan (${currentProgress}%)...`);
      } else {
        setPdfProgressText('Merender tata letak dokumen PDF ReportLab...');
        setProgressText(`Merender dokumen PDF (${currentProgress}%)...`);
      }
    }, 250);

    try {
      const params = new URLSearchParams({
        periode1: periode1.trim(),
        periode2: periode2.trim(),
        kd_klasifikasi: kdKlasifikasi.trim(),
        kategori: 'all'
      });
      const url = `${apiUrl}/cetak_kelengkapan_tagihan_pdf?${params.toString()}`;

      const response = await fetch(url);
      clearInterval(progressTimer);

      if (!response.ok) {
        throw new Error(`Server merespon dengan status ${response.status} (${response.statusText})`);
      }

      setPdfProgressVal(96);
      setPdfProgressText('Menerima berkas dokumen PDF...');
      setProgressVal(96);
      setProgressText('Menerima PDF (96%)...');

      const blob = await response.blob();
      const newBlobUrl = URL.createObjectURL(blob);

      setPdfProgressVal(100);
      setPdfProgressText('Dokumen PDF berhasil dibuat (100%)');
      setProgressVal(100);
      setProgressText('PDF Siap (100%)');
      setPdfBlobUrl(newBlobUrl);

      showToast('Dokumen PDF kelengkapan tagihan berhasil dimuat.', 'success', 3000);
    } catch (err) {
      clearInterval(progressTimer);
      console.error('Gagal membuat PDF:', err);
      setPdfProgressVal(0);
      setPdfProgressText('Gagal membuat dokumen PDF');
      setProgressText('Gagal membuat PDF');
      showToast(`Gagal membuat PDF: ${err.message}`, 'danger');
    } finally {
      setTimeout(() => {
        setIsGeneratingPdf(false);
      }, 350);
    }
  };

  // ── Filter Tabel Data Berdasarkan Keyword ──
  const filterList = useCallback((items) => {
    if (!tableSearch.trim()) return items;
    const q = tableSearch.toLowerCase();
    return items.filter(it =>
      (it.no_medrec || '').toLowerCase().includes(q) ||
      (it.nm_pasien || '').toLowerCase().includes(q) ||
      (it.no_kuitansi || '').toLowerCase().includes(q) ||
      (it.no_nota || '').toLowerCase().includes(q) ||
      (it.perusahaan || '').toLowerCase().includes(q)
    );
  }, [tableSearch]);

  const filteredResume = useMemo(() => filterList(dataResume), [dataResume, filterList]);
  const filteredLab = useMemo(() => filterList(dataLab), [dataLab, filterList]);
  const filteredRad = useMemo(() => filterList(dataRad), [dataRad, filterList]);

  const filteredKlasifikasi = useMemo(() => {
    if (!searchKlasifikasi.trim()) return klasifikasiList;
    const q = searchKlasifikasi.toLowerCase();
    return klasifikasiList.filter(k =>
      (k.kd_klas || '').toLowerCase().includes(q) ||
      (k.nm_klas || '').toLowerCase().includes(q)
    );
  }, [klasifikasiList, searchKlasifikasi]);

  // Reset pagination saat pencarian tabel berubah
  useEffect(() => {
    setPageResume(1);
    setPageLab(1);
    setPageRad(1);
  }, [tableSearch]);

  // Reset pagination modal klasifikasi saat kata kunci berubah
  useEffect(() => {
    setPageKlasifikasi(1);
  }, [searchKlasifikasi]);

  // ── Pagination Calculation & Slicing untuk Tab 1, 2, dan 3 ──
  const totalItemsResume = filteredResume.length;
  const totalPagesResume = Math.max(1, Math.ceil(totalItemsResume / itemsPerPage));
  const paginatedResume = useMemo(() => {
    const start = (pageResume - 1) * itemsPerPage;
    return filteredResume.slice(start, start + itemsPerPage);
  }, [filteredResume, pageResume, itemsPerPage]);

  const totalItemsLab = filteredLab.length;
  const totalPagesLab = Math.max(1, Math.ceil(totalItemsLab / itemsPerPage));
  const paginatedLab = useMemo(() => {
    const start = (pageLab - 1) * itemsPerPage;
    return filteredLab.slice(start, start + itemsPerPage);
  }, [filteredLab, pageLab, itemsPerPage]);

  const totalItemsRad = filteredRad.length;
  const totalPagesRad = Math.max(1, Math.ceil(totalItemsRad / itemsPerPage));
  const paginatedRad = useMemo(() => {
    const start = (pageRad - 1) * itemsPerPage;
    return filteredRad.slice(start, start + itemsPerPage);
  }, [filteredRad, pageRad, itemsPerPage]);

  // Pagination Modal Klasifikasi
  const totalItemsKlasifikasi = filteredKlasifikasi.length;
  const totalPagesKlasifikasi = Math.max(1, Math.ceil(totalItemsKlasifikasi / itemsPerPageKlasifikasi));
  const paginatedKlasifikasi = useMemo(() => {
    const start = (pageKlasifikasi - 1) * itemsPerPageKlasifikasi;
    return filteredKlasifikasi.slice(start, start + itemsPerPageKlasifikasi);
  }, [filteredKlasifikasi, pageKlasifikasi, itemsPerPageKlasifikasi]);

  // Helper mendapatkan nomor halaman aktif sesuai tab yang dipilih
  const getCurrentPage = () => {
    if (activeTab === 'resume') return pageResume;
    if (activeTab === 'lab') return pageLab;
    if (activeTab === 'rad') return pageRad;
    return 1;
  };

  const handlePageChange = (p) => {
    if (activeTab === 'resume') setPageResume(p);
    else if (activeTab === 'lab') setPageLab(p);
    else if (activeTab === 'rad') setPageRad(p);
  };

  // Format Angka Rupiah
  const formatRp = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      
      {/* ══════════════════════════════════════════════════════════════
          1. HEADER ATAS MODERN & COMPACT
      ══════════════════════════════════════════════════════════════ */}
      <header
        style={{
          background: 'linear-gradient(135deg, #091e3a 0%, #102a4e 50%, #1a365d 100%)',
          color: '#ffffff',
          padding: '6px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(9, 30, 58, 0.2)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.25) 0%, rgba(37, 99, 235, 0.35) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="bi bi-clipboard2-check-fill text-info" style={{ fontSize: '1.1rem' }}></i>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.92rem', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                Cek Kelengkapan Tagihan
              </span>
              <span
                style={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: 'rgba(56, 189, 248, 0.18)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.35)'
                }}
              >
                VERIFIKASI
              </span>
            </div>
            <div style={{ fontSize: '0.67rem', color: '#cbd5e1', lineHeight: 1.1 }}>
              Verifikasi Kelengkapan Berkas Tagihan Pasien &bull; Resume Medis, Hasil Laboratorium &amp; Radiologi
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Badge Total Kekurangan */}
          {hasSearched && (
            <div
              style={{
                background: counts.total > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                border: `1px solid ${counts.total > 0 ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
                borderRadius: 6,
                padding: '3px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <i className={`bi ${counts.total > 0 ? 'bi-exclamation-circle-fill text-danger' : 'bi-check-circle-fill text-success'}`} style={{ fontSize: '0.75rem' }} />
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: counts.total > 0 ? '#fca5a5' : '#86efac' }}>
                {counts.total > 0 ? `${counts.total} Berkas Belum Lengkap` : 'Semua Berkas Lengkap'}
              </div>
            </div>
          )}

          {/* User & Clock Compact */}
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="bi bi-clock text-info" style={{ fontSize: '0.68rem' }} />
              {time.clock}
            </div>
            <div style={{ fontSize: '0.64rem', color: '#94a3b8' }}>
              Petugas: <b style={{ color: '#e2e8f0' }}>{username}</b>
            </div>
          </div>

          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate('/home')}
            style={{
              fontWeight: 600,
              borderRadius: 6,
              fontSize: '0.7rem',
              padding: '3px 10px',
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.05)'
            }}
          >
            <i className="bi bi-arrow-left me-1" /> Kembali
          </Button>
        </div>
      </header>

      {/* ══ ALERT TOAST BANNER ══ */}
      {statusMsg.show && (
        <div style={{ padding: '6px 14px 0 14px' }}>
          <Alert
            variant={statusMsg.variant}
            onClose={() => setStatusMsg({ show: false, variant: 'info', text: '' })}
            dismissible
            className="mb-0 shadow-sm border-0"
            style={{
              fontSize: '0.74rem',
              borderRadius: 6,
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className={`bi bi-${statusMsg.variant === 'success' ? 'check-circle-fill text-success' : statusMsg.variant === 'danger' ? 'exclamation-octagon-fill text-danger' : statusMsg.variant === 'warning' ? 'exclamation-triangle-fill text-warning' : 'info-circle-fill text-info'} me-2`} style={{ fontSize: '0.9rem' }} />
            <div style={{ fontWeight: 500 }}>{statusMsg.text}</div>
          </Alert>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. MAIN CONTENT AREA (COMPACT GRID & TABBED RESULTS)
      ══════════════════════════════════════════════════════════════ */}
      <main style={{ flex: 1, padding: '8px 14px 50px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        
        {/* ── CARD 1: FORM FILTER (Sesuai Layout Gambar Mockup Gambas) ── */}
        <Card
          className="border-0 shadow-sm"
          style={{
            borderRadius: 8,
            background: '#ffffff',
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}
        >
          <div
            style={{
              background: '#091e3a',
              color: '#ffffff',
              padding: '5px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-funnel-fill text-info" style={{ fontSize: '0.8rem' }} />
              <span style={{ fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Parameter Pengecekan Penagihan
              </span>
            </div>
            <div style={{ fontSize: '0.67rem', color: '#93c5fd' }}>
              Filter Validasi Piutang &amp; Klasifikasi Pasien
            </div>
          </div>

          <Card.Body style={{ padding: '8px 14px', fontSize: '0.72rem' }}>
            <Row className="g-2 align-items-center">
              
              {/* BARIS 1: PERIODE VALIDASI S/D */}
              <Col md={6}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 140,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      borderRadius: 4,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      flexShrink: 0
                    }}
                  >
                    PERIODE VALIDASI
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 4 }}>
                    <Form.Control
                      ref={inputPeriode1Ref}
                      size="sm"
                      type="text"
                      placeholder="DD/MM/YYYY"
                      value={periode1}
                      onChange={(e) => setPeriode1(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inputPeriode2Ref.current) inputPeriode2Ref.current.focus();
                      }}
                      style={{ fontWeight: 700, fontSize: '0.72rem', padding: '3px 8px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 4 }}
                      title="Periode Awal Validasi (DD/MM/YYYY)"
                    />
                    <div style={{ background: '#0284c7', color: '#ffffff', fontWeight: 800, fontSize: '0.67rem', padding: '3px 8px', borderRadius: 4 }}>
                      S/D
                    </div>
                    <Form.Control
                      ref={inputPeriode2Ref}
                      size="sm"
                      type="text"
                      placeholder="DD/MM/YYYY"
                      value={periode2}
                      onChange={(e) => setPeriode2(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inputKlasRef.current) inputKlasRef.current.focus();
                      }}
                      style={{ fontWeight: 700, fontSize: '0.72rem', padding: '3px 8px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 4 }}
                      title="Periode Akhir Validasi (DD/MM/YYYY)"
                    />
                  </div>
                </div>
              </Col>

              {/* BARIS 2: KLASIFIKASI */}
              <Col md={6}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 140,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      borderRadius: 4,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      flexShrink: 0
                    }}
                  >
                    KLASIFIKASI
                  </div>
                  <div style={{ display: 'flex', flex: 1, gap: 4 }}>
                    <InputGroup size="sm" style={{ width: 95 }}>
                      <Form.Control
                        ref={inputKlasRef}
                        type="text"
                        placeholder="Kode"
                        value={kdKlasifikasi}
                        onChange={(e) => setKdKlasifikasi(e.target.value)}
                        onKeyDown={handleKlasifikasiKeyDown}
                        style={{ fontWeight: 700, fontSize: '0.72rem', padding: '3px 4px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px 0 0 4px' }}
                      />
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowModalKlasifikasi(true)}
                        title="Cari Klasifikasi"
                        style={{ borderRadius: '0 4px 4px 0', borderColor: '#cbd5e1', padding: '0 6px', fontSize: '0.7rem' }}
                      >
                        <i className="bi bi-search" />
                      </Button>
                    </InputGroup>
                    <Form.Control
                      size="sm"
                      type="text"
                      readOnly
                      placeholder="Semua Klasifikasi (Opsional)..."
                      value={nmKlasifikasi}
                      style={{
                        flex: 1,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        padding: '3px 8px',
                        background: '#f8fafc',
                        color: '#0369a1',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4
                      }}
                    />
                    {kdKlasifikasi && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => { setKdKlasifikasi(''); setNmKlasifikasi(''); }}
                        title="Reset Klasifikasi"
                        style={{ padding: '0 6px', fontSize: '0.7rem', borderRadius: 4 }}
                      >
                        <i className="bi bi-x-lg" />
                      </Button>
                    )}
                  </div>
                </div>
              </Col>

              {/* BARIS 3: LOKASI FILE LAPORAN / KETERANGAN */}
              <Col md={12} className="mt-2">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 140,
                      background: '#1e3a8a',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      borderRadius: 4,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      flexShrink: 0
                    }}
                  >
                    LOKASI LAPORAN
                  </div>
                  <InputGroup size="sm" style={{ flex: 1 }}>
                    <Form.Control
                      type="text"
                      readOnly
                      value={lokasiLaporan}
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.72rem',
                        padding: '3px 8px',
                        borderRadius: '4px 0 0 4px',
                        color: '#475569'
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleGeneratePdf('modal', false)}
                      disabled={isGeneratingPdf}
                      title="Buka Viewer PDF Langsung"
                      style={{ borderRadius: '0 4px 4px 0', borderColor: '#cbd5e1', padding: '0 10px', fontSize: '0.7rem' }}
                    >
                      {isGeneratingPdf ? (
                        <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                      ) : (
                        <i className="bi bi-file-earmark-pdf-fill text-danger me-1" />
                      )}
                      Lihat PDF
                    </Button>
                  </InputGroup>
                </div>
              </Col>

              {/* BARIS 4: PROGRESS BAR & ACTION BUTTONS (Sesuai Mockup) */}
              <Col md={12} className="mt-2 pt-2 border-top">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  
                  {/* Progress Bar Container (Mockup Gambas + Animasi PDF Progress) */}
                  <div style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 24, background: '#ffffff', border: '1px solid #94a3b8', borderRadius: 4, overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <ProgressBar
                        now={isGeneratingPdf ? pdfProgressVal : progressVal}
                        variant={
                          isGeneratingPdf
                            ? (pdfProgressVal === 100 ? 'success' : 'danger')
                            : (progressVal === 100 ? 'success' : 'primary')
                        }
                        animated={isGeneratingPdf || isLoading}
                        striped={isGeneratingPdf}
                        style={{ height: '100%', width: '100%', borderRadius: 0, background: '#f1f5f9', transition: 'width 0.25s ease' }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          width: '100%',
                          textAlign: 'center',
                          fontSize: '0.69rem',
                          fontWeight: 800,
                          color: (isGeneratingPdf ? pdfProgressVal : progressVal) > 50 ? '#ffffff' : '#0f172a',
                          textShadow: (isGeneratingPdf ? pdfProgressVal : progressVal) > 50 ? '0 1px 2px rgba(0,0,0,0.4)' : 'none'
                        }}
                      >
                        {isGeneratingPdf
                          ? (pdfProgressText || `Membuat Dokumen PDF... ${pdfProgressVal}%`)
                          : isLoading
                          ? progressText
                          : hasSearched
                          ? `Selesai (${counts.total} belum lengkap)`
                          : 'Siap periksa'}
                      </div>
                    </div>
                  </div>

                  {/* Buttons: PROSES / PRINT, VIEWER, KELUAR */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isLoading || isGeneratingPdf}
                      onClick={handleProsesPengecekan}
                      style={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        padding: '5px 16px',
                        borderRadius: 5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        border: 'none'
                      }}
                    >
                      {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-play-circle-fill" />}
                      PROSES CEK
                    </Button>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={isGeneratingPdf || isLoading}
                      onClick={() => handleGeneratePdf('modal', true)}
                      title="Generate dan cetak dokumen PDF kelengkapan tagihan"
                      style={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        padding: '5px 14px',
                        borderRadius: 5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        boxShadow: isGeneratingPdf ? '0 0 0 2px rgba(220, 38, 38, 0.3)' : 'none'
                      }}
                    >
                      {isGeneratingPdf ? (
                        <>
                          <Spinner size="sm" animation="border" />
                          <span>{pdfProgressVal > 0 ? `MEMBUAT PDF ${pdfProgressVal}%` : 'MEMBUAT PDF...'}</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-printer-fill" />
                          CETAK / LIHAT PDF
                        </>
                      )}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setDataResume([]);
                        setDataLab([]);
                        setDataRad([]);
                        setCounts({ resume: 0, lab: 0, rad: 0, total: 0 });
                        setHasSearched(false);
                        setProgressVal(0);
                        setPageResume(1);
                        setPageLab(1);
                        setPageRad(1);
                      }}
                      style={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        padding: '5px 12px',
                        borderRadius: 5
                      }}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1" />
                      Reset
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => navigate('/home')}
                      style={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        padding: '5px 14px',
                        borderRadius: 5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <i className="bi bi-x-circle-fill" />
                      KELUAR
                    </Button>
                  </div>

                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>


        {/* ── CARD 2: TABEL HASIL PENGECEKAN KELENGKAPAN (Tabbed Categories + Inline PDF) ── */}
        <Card
          className="border-0 shadow-sm flex-grow-1"
          style={{
            borderRadius: 8,
            background: '#ffffff',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Tabs Bar Header */}
          <div
            style={{
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              padding: '6px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}
          >
            {/* Category Nav Tabs */}
            <Nav variant="pills" style={{ gap: 4 }}>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'resume'}
                  onClick={() => setActiveTab('resume')}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: 5,
                    cursor: 'pointer',
                    background: activeTab === 'resume' ? '#1e3a8a' : 'transparent',
                    color: activeTab === 'resume' ? '#ffffff' : '#334155'
                  }}
                >
                  <i className="bi bi-file-earmark-medical me-1" />
                  1. Belum Resume Medis
                  <Badge bg={counts.resume > 0 ? 'danger' : 'secondary'} className="ms-2" style={{ fontSize: '0.62rem' }}>
                    {counts.resume}
                  </Badge>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'lab'}
                  onClick={() => setActiveTab('lab')}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: 5,
                    cursor: 'pointer',
                    background: activeTab === 'lab' ? '#047857' : 'transparent',
                    color: activeTab === 'lab' ? '#ffffff' : '#334155'
                  }}
                >
                  <i className="bi bi-droplet-half me-1" />
                  2. Belum Hasil Lab
                  <Badge bg={counts.lab > 0 ? 'danger' : 'secondary'} className="ms-2" style={{ fontSize: '0.62rem' }}>
                    {counts.lab}
                  </Badge>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'rad'}
                  onClick={() => setActiveTab('rad')}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: 5,
                    cursor: 'pointer',
                    background: activeTab === 'rad' ? '#b45309' : 'transparent',
                    color: activeTab === 'rad' ? '#ffffff' : '#334155'
                  }}
                >
                  <i className="bi bi-radioactive me-1" />
                  3. Belum Hasil Radiologi
                  <Badge bg={counts.rad > 0 ? 'danger' : 'secondary'} className="ms-2" style={{ fontSize: '0.62rem' }}>
                    {counts.rad}
                  </Badge>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'pdf'}
                  onClick={() => {
                    handleGeneratePdf('tab', false);
                  }}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: 5,
                    cursor: 'pointer',
                    background: activeTab === 'pdf' ? '#475569' : 'transparent',
                    color: activeTab === 'pdf' ? '#ffffff' : '#334155'
                  }}
                >
                  <i className="bi bi-file-earmark-pdf-fill text-danger me-1" />
                  Lihat Dokumen PDF Langsung
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* Quick Search inside Table */}
            {activeTab !== 'pdf' && (
              <InputGroup size="sm" style={{ width: 220 }}>
                <InputGroup.Text style={{ padding: '0 6px', background: '#ffffff', borderColor: '#cbd5e1' }}>
                  <i className="bi bi-search text-muted" style={{ fontSize: '0.7rem' }} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Cari RM / Nama / Nota..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  style={{ fontSize: '0.7rem', padding: '3px 6px', borderColor: '#cbd5e1' }}
                />
              </InputGroup>
            )}
          </div>

          {/* Body Tabel / Inline PDF Viewer */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 340 }}>
            
            {/* ── TAB 1: BELUM RESUME MEDIS ── */}
            {activeTab === 'resume' && (
              <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
                <thead style={{ background: '#1e3a8a', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center', padding: '6px 4px' }}>#</th>
                    <th style={{ width: 95, textAlign: 'center', padding: '6px 6px' }}>No. KIRS / RM</th>
                    <th style={{ padding: '6px 8px' }}>Nama Pasien</th>
                    <th style={{ width: 140, padding: '6px 6px' }}>No. Kuitansi</th>
                    <th style={{ width: 150, padding: '6px 6px' }}>No. Rincian / Nota</th>
                    <th style={{ width: 110, textAlign: 'right', padding: '6px 8px' }}>Total Tagihan</th>
                    <th style={{ padding: '6px 8px' }}>Perusahaan Penjamin</th>
                    <th style={{ width: 85, textAlign: 'center', padding: '6px 4px' }}>Tgl Validasi</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResume.length > 0 ? (
                    paginatedResume.map((it, idx) => {
                      const rowNumber = (pageResume - 1) * itemsPerPage + idx + 1;
                      return (
                        <tr key={idx}>
                          <td className="text-center text-muted">{rowNumber}</td>
                          <td className="font-monospace fw-bold text-center text-primary">{it.no_medrec}</td>
                          <td className="fw-semibold text-dark">{it.nm_pasien}</td>
                          <td className="font-monospace text-muted">{it.no_kuitansi}</td>
                          <td className="font-monospace fw-semibold text-danger">{it.no_nota}</td>
                          <td className="text-end fw-bold">{formatRp(it.total)}</td>
                          <td className="text-muted">{it.perusahaan}</td>
                          <td className="text-center text-muted">{it.tgl_validasi}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        {hasSearched
                          ? 'Tidak ada pasien rawat inap yang belum memiliki Resume Medis (Lengkap / Nihil).'
                          : 'Silakan tentukan periode validasi dan klik tombol "PROSES CEK" untuk memulai pengecekan.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {/* ── TAB 2: BELUM HASIL LABORATORIUM ── */}
            {activeTab === 'lab' && (
              <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
                <thead style={{ background: '#047857', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center', padding: '6px 4px' }}>#</th>
                    <th style={{ width: 95, textAlign: 'center', padding: '6px 6px' }}>No. KIRS / RM</th>
                    <th style={{ padding: '6px 8px' }}>Nama Pasien</th>
                    <th style={{ width: 140, padding: '6px 6px' }}>No. Kuitansi</th>
                    <th style={{ width: 150, padding: '6px 6px' }}>No. Nota LAB</th>
                    <th style={{ padding: '6px 8px' }}>Nama Pemeriksaan</th>
                    <th style={{ width: 110, textAlign: 'right', padding: '6px 8px' }}>Tarif Lab</th>
                    <th style={{ padding: '6px 8px' }}>Perusahaan Penjamin</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLab.length > 0 ? (
                    paginatedLab.map((it, idx) => {
                      const rowNumber = (pageLab - 1) * itemsPerPage + idx + 1;
                      return (
                        <tr key={idx}>
                          <td className="text-center text-muted">{rowNumber}</td>
                          <td className="font-monospace fw-bold text-center text-success">{it.no_medrec}</td>
                          <td className="fw-semibold text-dark">{it.nm_pasien}</td>
                          <td className="font-monospace text-muted">{it.no_kuitansi}</td>
                          <td className="font-monospace fw-semibold text-danger">{it.no_nota}</td>
                          <td className="text-muted">{it.nm_tindakan}</td>
                          <td className="text-end fw-bold">{formatRp(it.total)}</td>
                          <td className="text-muted">{it.perusahaan}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        {hasSearched
                          ? 'Tidak ada tindakan laboratorium yang belum memiliki hasil lab (Lengkap / Nihil).'
                          : 'Silakan tentukan periode validasi dan klik tombol "PROSES CEK" untuk memulai pengecekan.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {/* ── TAB 3: BELUM HASIL RADIOLOGI ── */}
            {activeTab === 'rad' && (
              <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
                <thead style={{ background: '#b45309', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center', padding: '6px 4px' }}>#</th>
                    <th style={{ width: 95, textAlign: 'center', padding: '6px 6px' }}>No. KIRS / RM</th>
                    <th style={{ padding: '6px 8px' }}>Nama Pasien</th>
                    <th style={{ width: 140, padding: '6px 6px' }}>No. Kuitansi</th>
                    <th style={{ width: 150, padding: '6px 6px' }}>No. Nota RAD</th>
                    <th style={{ padding: '6px 8px' }}>Nama Pemeriksaan</th>
                    <th style={{ width: 110, textAlign: 'right', padding: '6px 8px' }}>Tarif Rad</th>
                    <th style={{ padding: '6px 8px' }}>Perusahaan Penjamin</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRad.length > 0 ? (
                    paginatedRad.map((it, idx) => {
                      const rowNumber = (pageRad - 1) * itemsPerPage + idx + 1;
                      return (
                        <tr key={idx}>
                          <td className="text-center text-muted">{rowNumber}</td>
                          <td className="font-monospace fw-bold text-center text-warning">{it.no_medrec}</td>
                          <td className="fw-semibold text-dark">{it.nm_pasien}</td>
                          <td className="font-monospace text-muted">{it.no_kuitansi}</td>
                          <td className="font-monospace fw-semibold text-danger">{it.no_nota}</td>
                          <td className="text-muted">{it.nm_tindakan}</td>
                          <td className="text-end fw-bold">{formatRp(it.total)}</td>
                          <td className="text-muted">{it.perusahaan}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        {hasSearched
                          ? 'Tidak ada tindakan radiologi yang belum memiliki hasil radiologi (Lengkap / Nihil).'
                          : 'Silakan tentukan periode validasi dan klik tombol "PROSES CEK" untuk memulai pengecekan.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {/* ── TAB 4: INLINE PDF VIEWER LANGSUNG DI HALAMAN (TANPA BUKA PAGE BARU) ── */}
            {activeTab === 'pdf' && (
              <div style={{ width: '100%', height: '65vh', background: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#334155', color: '#ffffff', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem' }}>
                  <span>
                    <i className="bi bi-file-earmark-pdf-fill text-danger me-1" />
                    Viewer Dokumen Laporan PDF Resmi (Periode: {periode1} s.d {periode2})
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={isGeneratingPdf}
                      onClick={() => handleGeneratePdf('tab', true)}
                      style={{ fontSize: '0.67rem', padding: '2px 8px' }}
                    >
                      <i className="bi bi-arrow-clockwise me-1" /> Muat Ulang PDF
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      disabled={!pdfBlobUrl || isGeneratingPdf}
                      onClick={() => {
                        if (pdfBlobUrl) window.open(pdfBlobUrl, '_blank');
                      }}
                      style={{ fontSize: '0.67rem', padding: '2px 8px', fontWeight: 600 }}
                    >
                      <i className="bi bi-box-arrow-up-right me-1" /> Buka Tab Terpisah
                    </Button>
                  </div>
                </div>

                {isGeneratingPdf ? (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
                      color: '#ffffff',
                      padding: 24
                    }}
                  >
                    <div
                      style={{
                        background: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid #334155',
                        borderRadius: 12,
                        padding: '24px 30px',
                        maxWidth: 480,
                        width: '92%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ position: 'relative', display: 'inline-block', marginBottom: 14 }}>
                        <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: '3rem' }} />
                        <span
                          className="spinner-border spinner-border-sm text-info"
                          style={{ position: 'absolute', right: -6, bottom: 2, width: 16, height: 16 }}
                        />
                      </div>
                      <h6 style={{ fontWeight: 800, fontSize: '0.95rem', color: '#f8fafc', marginBottom: 6 }}>
                        Sedang Membuat Dokumen PDF
                      </h6>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 16 }}>
                        Mengompilasi data kelengkapan tagihan periode <strong className="text-white">{periode1} s.d {periode2}</strong>...
                      </p>

                      <div style={{ marginBottom: 12 }}>
                        <ProgressBar
                          animated
                          striped
                          now={pdfProgressVal}
                          variant={pdfProgressVal === 100 ? 'success' : 'danger'}
                          style={{ height: 20, borderRadius: 6, fontSize: '0.7rem', fontWeight: 800 }}
                          label={`${pdfProgressVal}%`}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem', color: '#cbd5e1' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Spinner size="sm" animation="border" variant="light" style={{ width: 12, height: 12 }} />
                          {pdfProgressText || 'Memproses berkas...'}
                        </span>
                        <span className="fw-bold text-info">{pdfProgressVal}%</span>
                      </div>
                    </div>
                  </div>
                ) : pdfBlobUrl ? (
                  <iframe
                    title="Laporan Kelengkapan Tagihan PDF"
                    src={pdfBlobUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-dark text-white p-4">
                    <i className="bi bi-file-earmark-pdf text-muted mb-2" style={{ fontSize: '2.5rem' }} />
                    <p style={{ fontSize: '0.78rem', color: '#cbd5e1', marginBottom: 14 }}>
                      Dokumen PDF belum digenerate. Klik tombol di bawah untuk membuat dokumen PDF.
                    </p>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleGeneratePdf('tab', true)}
                      style={{ fontSize: '0.72rem', fontWeight: 700, padding: '5px 16px' }}
                    >
                      <i className="bi bi-play-fill me-1" /> Buat Dokumen PDF
                    </Button>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ── PAGINATION CONTROLS UNTUK TAB AKTIF (Resume / Lab / Rad) ── */}
          {activeTab !== 'pdf' && (
            <PaginationBar
              currentPage={getCurrentPage()}
              totalPages={
                activeTab === 'resume' ? totalPagesResume :
                activeTab === 'lab' ? totalPagesLab :
                totalPagesRad
              }
              totalItems={
                activeTab === 'resume' ? totalItemsResume :
                activeTab === 'lab' ? totalItemsLab :
                totalItemsRad
              }
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={(val) => {
                setItemsPerPage(val);
                setPageResume(1);
                setPageLab(1);
                setPageRad(1);
              }}
              itemLabel={
                activeTab === 'resume' ? 'berkas belum resume' :
                activeTab === 'lab' ? 'tindakan lab belum hasil' :
                'tindakan radiologi belum hasil'
              }
              itemsPerPageOptions={[10, 25, 50, 100]}
              activeColor={
                activeTab === 'resume' ? '#1e3a8a' :
                activeTab === 'lab' ? '#047857' :
                '#b45309'
              }
            />
          )}

          {/* Footer Card Subtotal Count */}
          <div
            style={{
              background: '#f1f5f9',
              borderTop: '1px solid #e2e8f0',
              padding: '6px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.7rem'
            }}
          >
            <div style={{ display: 'flex', gap: 14 }}>
              <span>Resume: <strong style={{ color: '#1e3a8a' }}>{counts.resume}</strong></span>
              <span>Lab: <strong style={{ color: '#047857' }}>{counts.lab}</strong></span>
              <span>Radiologi: <strong style={{ color: '#b45309' }}>{counts.rad}</strong></span>
            </div>
            <div>
              Total Kekurangan: <strong style={{ color: counts.total > 0 ? '#dc2626' : '#15803d' }}>{counts.total} Berkas</strong>
            </div>
          </div>
        </Card>

      </main>

      {/* ══════════════════════════════════════════════════════════════
          3. MODAL DIALOGS (LOOKUP KLASIFIKASI & FULL PDF MODAL)
      ══════════════════════════════════════════════════════════════ */}

      {/* ══ MODAL CARI KLASIFIKASI TARIF ══ */}
      <Modal show={showModalKlasifikasi} onHide={() => setShowModalKlasifikasi(false)} size="md" centered>
        <Modal.Header closeButton style={{ background: '#0f2b48', color: '#fff', padding: '8px 14px', border: 'none' }}>
          <Modal.Title style={{ fontSize: '0.84rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-search text-info" />
            Pilih Klasifikasi Tarif Pasien
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2" style={{ background: '#f8fafc' }}>
          <InputGroup size="sm" className="mb-2 shadow-sm">
            <InputGroup.Text style={{ background: '#ffffff', borderColor: '#cbd5e1', padding: '3px 8px' }}>
              <i className="bi bi-search text-muted" style={{ fontSize: '0.75rem' }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Cari Kode atau Nama Klasifikasi..."
              value={searchKlasifikasi}
              onChange={(e) => setSearchKlasifikasi(e.target.value)}
              style={{ borderColor: '#cbd5e1', fontSize: '0.74rem', padding: '4px 8px' }}
              autoFocus
            />
          </InputGroup>

          <div style={{ maxHeight: '280px', overflowY: 'auto', borderRadius: 6, border: '1px solid #e2e8f0', background: '#ffffff' }}>
            <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
              <thead style={{ background: '#1e293b', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 35, textAlign: 'center', padding: '6px 4px' }}>#</th>
                  <th style={{ width: 80, textAlign: 'center', padding: '6px 8px' }}>Kode</th>
                  <th style={{ padding: '6px 8px' }}>Nama Klasifikasi</th>
                  <th style={{ width: 65, textAlign: 'center', padding: '6px 8px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingKlasifikasi ? (
                  <tr>
                    <td colSpan="4" className="text-center py-3 text-primary">
                      <Spinner size="sm" animation="border" className="me-2" />
                      Memuat master klasifikasi...
                    </td>
                  </tr>
                ) : paginatedKlasifikasi.length > 0 ? (
                  paginatedKlasifikasi.map((item, idx) => {
                    const rowNumber = (pageKlasifikasi - 1) * itemsPerPageKlasifikasi + idx + 1;
                    return (
                      <tr key={idx}>
                        <td className="text-center text-muted">{rowNumber}</td>
                        <td className="fw-bold font-monospace text-center text-primary">{item.kd_klas}</td>
                        <td className="fw-semibold text-dark">{item.nm_klas}</td>
                        <td className="text-center">
                          <Button
                            variant="primary"
                            size="sm"
                            style={{ padding: '2px 8px', fontSize: '0.67rem', borderRadius: 4, fontWeight: 700 }}
                            onClick={() => handleSelectKlasifikasi(item)}
                          >
                            Pilih
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-3 text-muted">
                      Tidak ada klasifikasi tarif ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Modal Klasifikasi */}
          <div className="mt-1">
            <PaginationBar
              currentPage={pageKlasifikasi}
              totalPages={totalPagesKlasifikasi}
              totalItems={totalItemsKlasifikasi}
              itemsPerPage={itemsPerPageKlasifikasi}
              onPageChange={setPageKlasifikasi}
              itemLabel="klasifikasi"
              activeColor="#0f2b48"
            />
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL VIEWER PDF LANGSUNG (TANPA BUKA PAGE BARU) ══ */}
      <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: '#0f2b48', color: '#fff', padding: '8px 16px', border: 'none' }}>
          <Modal.Title style={{ fontSize: '0.86rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-file-earmark-pdf-fill text-danger" />
            Laporan Pengecekan Kelengkapan Penagihan (PDF)
            {isGeneratingPdf && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.65rem' }}>
                <Spinner size="sm" animation="border" style={{ width: 10, height: 10 }} className="me-1" />
                Membuat Dokumen ({pdfProgressVal}%)
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh', background: '#1e293b', position: 'relative' }}>
          {isGeneratingPdf ? (
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
                color: '#ffffff',
                padding: 24
              }}
            >
              <div
                style={{
                  background: 'rgba(30, 41, 59, 0.95)',
                  border: '1px solid #334155',
                  borderRadius: 12,
                  padding: '28px 34px',
                  maxWidth: 500,
                  width: '92%',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
                  textAlign: 'center'
                }}
              >
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
                  <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: '3.4rem' }} />
                  <span
                    className="spinner-border spinner-border-sm text-info"
                    style={{ position: 'absolute', right: -6, bottom: 4, width: 18, height: 18 }}
                  />
                </div>

                <h5 style={{ fontWeight: 800, fontSize: '1rem', color: '#f8fafc', marginBottom: 6 }}>
                  Sedang Menyiapkan Dokumen PDF
                </h5>
                <p style={{ fontSize: '0.74rem', color: '#94a3b8', marginBottom: 16 }}>
                  Memproses kompilasi data pengecekan kelengkapan tagihan periode{' '}
                  <strong className="text-white">{periode1} s.d {periode2}</strong>...
                </p>

                {/* Progress Bar Utama Modal */}
                <div style={{ marginBottom: 12 }}>
                  <ProgressBar
                    animated
                    striped
                    now={pdfProgressVal}
                    variant={pdfProgressVal === 100 ? 'success' : 'danger'}
                    style={{ height: 22, borderRadius: 6, fontSize: '0.72rem', fontWeight: 800 }}
                    label={`${pdfProgressVal}%`}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#cbd5e1' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Spinner size="sm" animation="border" variant="light" style={{ width: 12, height: 12 }} />
                    {pdfProgressText || 'Memproses dokumen PDF...'}
                  </span>
                  <span className="fw-bold text-info">{pdfProgressVal}%</span>
                </div>
              </div>
            </div>
          ) : pdfBlobUrl ? (
            <iframe
              title="Laporan Kelengkapan Tagihan PDF Modal"
              src={pdfBlobUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white p-4">
              <i className="bi bi-exclamation-triangle text-warning mb-2" style={{ fontSize: '2.5rem' }} />
              <p style={{ fontSize: '0.82rem', marginBottom: 14 }}>Dokumen PDF belum digenerate atau gagal dimuat.</p>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleGeneratePdf('modal', true)}
                style={{ fontSize: '0.74rem', fontWeight: 700, padding: '6px 16px' }}
              >
                <i className="bi bi-arrow-clockwise me-1" /> Buat Dokumen PDF Sekarang
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 14px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem', color: '#64748b' }}>
            {isGeneratingPdf ? (
              <span className="text-primary fw-semibold">
                <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} className="me-1" />
                {pdfProgressText} ({pdfProgressVal}%)
              </span>
            ) : pdfBlobUrl ? (
              <span className="text-success fw-semibold">
                <i className="bi bi-check-circle-fill me-1" /> Dokumen PDF siap dicetak / diunduh
              </span>
            ) : null}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowPdfModal(false)}
            style={{ fontSize: '0.72rem', padding: '4px 12px' }}
          >
            Tutup
          </Button>

          {pdfBlobUrl && (
            <Button
              variant="outline-danger"
              size="sm"
              disabled={isGeneratingPdf}
              onClick={() => {
                const link = document.createElement('a');
                link.href = pdfBlobUrl;
                link.download = `Kelengkapan_Tagihan_${periode1.replace(/\//g, '-')}_sd_${periode2.replace(/\//g, '-')}.pdf`;
                link.click();
              }}
              style={{ fontSize: '0.72rem', padding: '4px 12px', fontWeight: 600 }}
            >
              <i className="bi bi-download me-1" /> Unduh PDF
            </Button>
          )}

          <Button
            variant="primary"
            size="sm"
            disabled={!pdfBlobUrl || isGeneratingPdf}
            onClick={() => {
              if (pdfBlobUrl) window.open(pdfBlobUrl, '_blank');
            }}
            style={{ fontSize: '0.72rem', padding: '4px 14px', fontWeight: 600 }}
          >
            <i className="bi bi-box-arrow-up-right me-1" /> Buka Tab Terpisah
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default CekKelengkapanTagihan;
